import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ViewContainerRef, Injector, AfterViewInit, QueryList, ViewChildren } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Column, ObjectProperties } from '../../../../../../../../../common/components/data-table/models/column.interface';
import { componentFrameworks } from '../../../../../../../../../common/components/data-table/data-table.component';
import { ValueFormatter } from '../../../../../../../../../common/components/data-table/formatters/data-table.formatters';
import { ArraySelectorOptionsComponent } from './formatter-settings/array-selector-options/array-selector-options.component';
import { DATA_TOKEN } from '../../grid-option-config/tokens';
import { FlagEnumOptionsComponent } from './formatter-settings/flagEnum-options/flagEnum-options.component';
import { DomainOptionsComponent } from './formatter-settings/domain-options/domain-options.component';

@Component({
    selector: 'column-item',
    templateUrl: './column-item.component.html',
    styleUrls: ['./column-item.component.scss'],
    standalone: false
})
export class ColumnItemComponent implements AfterViewInit {
    @ViewChildren('formatterSettings', { read: ViewContainerRef })
    formatterSettingsContainer: QueryList<ViewContainerRef>;

    public _properties: ObjectProperties[];
    public _column: Column;

    editing: boolean = false;
    formatterTypes = Object.values(ValueFormatter);
    rendererTypes = Object.keys(componentFrameworks);

    get column() { return this._column; }

    @Input()
    set column(val: Column) {
        this._column = val;

        if (!this.column)
            return;

        if (!this.column.formatterOptions)
            this.column.formatterOptions = {
                params: {}
            }

        if (this.column.filterable == null)
            this.column.filterable = true;

        if (this.column.sortable == null)
            this.column.sortable = true;

        if (this.column.resizable == null)
            this.column.resizable = true;

        this.form = new FormGroup({
            name: new FormControl(this.column.name, Validators.required),
            renderer: new FormControl(this.column.renderer || 'default'),
            formatter: new FormControl(this.column.valueFormatter),
            filterable: new FormControl(this.column.filterable),
            sortable: new FormControl(this.column.sortable),
            resizable: new FormControl(this.column.resizable),
            formatterParameters: new FormControl(this.column.formatterOptions.params),
            minWidth: new FormControl(this.column.minWidth || null)
        });

        this.form.controls.formatter.valueChanges.subscribe((result: ValueFormatter) => {
            this._setValueFormatterOptions(result);
        });

        if (this.formatterSettingsContainer && this.formatterSettingsContainer.length > 0)
            this._setValueFormatterOptions(this.column.valueFormatter);
    }

    @Input()
    set properties(val: ObjectProperties[]) {
        this._properties = val;
        this._changeDetectorRef.detectChanges();
    }

    get properties() { return this._properties; }

    @Output()
    onDelete: EventEmitter<Column> = new EventEmitter();
    @Output()
    onEdit: EventEmitter<Column> = new EventEmitter();

    form: FormGroup = new FormGroup({
        name: new FormControl(null, Validators.required),
        property: new FormControl(),
        minWidth: new FormControl(),
        resizable: new FormControl(),
        filterable: new FormControl(),
        sortable: new FormControl(),
        renderer: new FormControl('default'),
        formatter: new FormControl(null),
    });

    constructor(
        public _changeDetectorRef: ChangeDetectorRef,
        public _injector: Injector
    ) { }

    ngAfterViewInit() {
        this.formatterSettingsContainer.changes.subscribe((result: QueryList<ViewContainerRef>) => {
            if (result.length > 0)
                this._setValueFormatterOptions(this.column.valueFormatter);
        });
    }

    delete(event?: MouseEvent): void {
        this.onDelete.emit(this.column);
    }

    toggleEdit(event?: MouseEvent): void {
        this.editing = !this.editing;

        if (this.editing)
            this.onEdit.emit(this.column);
        else {
            this.column.name = this.form.get('name').value;
            this.column.filterable = this.form.get('filterable').value;
            this.column.resizable = this.form.get('resizable').value;
            this.column.sortable = this.form.get('sortable').value;
            this.column.renderer = this.form.get('renderer').value;
            this.column.valueFormatter = this.form.get('formatter').value;
            this.column.formatterOptions = { params: this.form.get('formatterParameters').value };
            this.column.minWidth = this.form.get('minWidth').value;
        }
    }

    public _setValueFormatterOptions(formatter: ValueFormatter): void {
        this.formatterSettingsContainer.first.clear();
        let components = [];

        if (formatter !== this.column.valueFormatter)
            this.form.controls['formatterParameters'].setValue(null);

        switch (formatter) {
            case ValueFormatter.ArraySelector:
                components = [ArraySelectorOptionsComponent];
                break;
            case ValueFormatter.FlagEnum:
                components = [FlagEnumOptionsComponent];
                break;
            case ValueFormatter.Domain:
                components = [DomainOptionsComponent];
                break;
            default:
                break;
        }

		components.forEach(x => {
			let compRef = this.formatterSettingsContainer.first.createComponent(
				x,
				{ injector: this._createInjector(this.form.get('formatterParameters')) }
			);
			compRef.changeDetectorRef.detectChanges();
			this._changeDetectorRef.detectChanges();
		});
    }

    public _createInjector(data: any): Injector {
		return Injector.create({
			providers: [{ provide: DATA_TOKEN, useValue: data }],
			parent: this._injector
		});
    }

    setSelectedProperty(prop: ObjectProperties) {
        this.column.objectProperty = prop;
    }
}